/**
 * record-store.js
 */

import React from 'react';
import axios from 'axios';
import { hookstate, useHookstate, none } from '@hookstate/core';
import {
  parseISO,
  format,
  formatISO,
  eachDayOfInterval,
  endOfMonth,
  endOfDay,
  addMonths
} from 'date-fns';
import { Types } from 'utils/constants';
import { dateParamFormat, calendarMonthBuffer } from 'utils/datetime';
import logger from 'utils/logger';
import analytics from 'utils/analytics';
import { objectToFormData } from "utils/form-data";
import { objectToUrlParams } from 'utils/misc';
import { useTranslation } from 'react-i18next';
import useRecordList from 'contexts/record-list';
import { UserContext } from 'contexts/user/user-context';
import { getPatrolSummary } from 'routing/routes/records/record-list/helpers';
import { getSizeTypeAnalyticsLabel } from 'components/training/training-utils';
import TrainingHandler from './training-handler';
import DeploymentHandler from './deployment-handler';
import ClassHandler from './class-handler';
import VetVisitHandler from './vet-visit-handler';


const trainingHandler = TrainingHandler();
const deploymentHandler = DeploymentHandler();
const classHandler = ClassHandler();
const vetVisitHandler = VetVisitHandler();


const recordsState = hookstate({
  // duplicate request protection
  fetching: {},

  // training indexes
  training: {
    event: {},
    exercise: {},
    answer: {},
  },

  // deployment index
  deployment: {},

  // training class index
  trainingClass: {},

  // vet visit index
  vetVisit: {},

  // calendar
  calendar: {
    date: {}, // date-based index
    selectedDates: [], // list of selected dates
    chunkStartDate: null,
    isLoading: false,
    isFetching: false,
  },

  status: {
    filterParams: {},
    isInitializing: true,
    isLoading: true,
    isBatchFetching: false, // used when batch-fetching to prevent duplicate requests
    chunkStartDate: '',
    stats: {
      recordCount: 0,
      incompleteCount: 0,
      rejectedCount: 0,
      outdatedCount: 0,
      lateCompletionCount: 0,
      notReviewedCount: 0,
      deploymentTrackMapCount: 0,
      overdueVaccinationCount: 0,
      hasLiveDeploymentTracks: false,
      isLiveMapDialogOpen: false,
    },
  }
});


export default function useRecordStore() {
  const state = useHookstate(recordsState);
  const recordList = useRecordList(recordsState);
  const { t } = useTranslation('general');
  const { user } = React.useContext(UserContext);
  const emptyDateRecord = { selected: false, records: [] };
  const numExtraPagesBuffer = 4;


  // ---------------------------------------------------------------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------
  // RECORD FETCHING AND PROCESSING
  // ---------------------------------------------------------------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------


  /**
   * resets record list and store status params, also navigates to /records
   */
  const reset = () => {
    recordList.reset();
    state.fetching.set({});
    state.training.event.set({});
    state.training.exercise.set({});
    state.training.answer.set({});
    state.deployment.set({});

    resetCalendar(state);

    state.status.filterParams.set({});
    state.status.isInitializing.set(true);
    state.status.isLoading.set(true);
    state.status.chunkStartDate.set('');
    state.status.stats.recordCount.set(0);
    state.status.stats.incompleteCount.set(0);
    state.status.stats.rejectedCount.set(0);
    state.status.stats.outdatedCount.set(0);
    state.status.stats.lateCompletionCount.set(0);
    state.status.stats.notReviewedCount.set(0);
    state.status.stats.overdueVaccinationCount.set(0);
    state.status.stats.deploymentTrackMapCount.set(0);
    state.status.stats.hasLiveDeploymentTracks.set(false);
    state.status.stats.isLiveMapDialogOpen.set(false);
  }


  /**
   * resets calendar data
   */
  const resetCalendar = (s) => {
    s.calendar.date.set({});
    s.calendar.selectedDates.set([]);
    s.calendar.chunkStartDate.set(null);
    s.calendar.isLoading.set(false);
  }


  /**
   * entry point for setting records page state
   */
  const setFilter = async (filterParams, pageNum = 1, force = false) => {
    var data;
    const isFilterChanged = force
      || state.status.isInitializing.get()
      || JSON.stringify(filterParams) !== JSON.stringify(state.status.filterParams.get());

    if (isFilterChanged) {
      // INITIALIZE
      if (state.status.isInitializing.get()) {
        logger.log('[record-store.js] Initializing record store');

        // if initializing with date
        if (filterParams.date) {
          data = await fetchRecords({
            filterParams: {
              init: true,
              handlers: filterParams.handlers || [],
              startDate: filterParams.date,
              endDate: filterParams.date
            },
            noListProcessing: true
          });
        }
      }

      logger.log('[record-store.js] Setting filter. Params: %s, pageNum: %d', filterParams, pageNum);

      // check if handlers has changed for resetting calendar and getting new data
      const currHandlers = JSON.stringify(state.status.filterParams.handlers.get() || '');
      const newHandlers = JSON.stringify(filterParams.handlers || '')
      const handlersChanged = currHandlers !== newHandlers;

      recordList.reset(pageNum);
      state.status.isLoading.set(true);
      state.status.filterParams.set({ ...filterParams });

      if (!state.status.isInitializing.get() && handlersChanged) {
        resetCalendar(state);
        state.calendar.isLoading.set(true);
      }

      // DATE FILTER
      if (filterParams.date) {
        const dateState = state.calendar.date[filterParams.date].ornull;
        const records = dateState ? dateState.records.get() : [];

        // build list of records that need to be requested
        let requestRecords = {
          events: [],
          deployments: [],
          classes: []
        };

        for (let i = 0; i < records.length; i++) {
          const record = records[i];
          const index = recordHandler(record.recordType).recordIndex(record.recordType, state);

          if (!index[record.id].ornull) {
            if (record.recordType === Types.Record.EVENT) {
              requestRecords.events.push(record.id);
            } else if (record.recordType === Types.Record.DEPLOYMENT) {
              requestRecords.deployments.push(record.id);
            } else if (record.recordType === Types.Record.CLASS) {
              requestRecords.classes.push(record.id);
            }
          }
        }

        await fetchRecordsList(requestRecords.events, requestRecords.deployments, requestRecords.classes);
        recordList.processChunk(records, undefined);

        // this call inside a batch causes unnecessary rendering (?)
        setSelectedDates([filterParams.date]);
      }

      // ALL OTHER FILTERS
      else {
        if (state.status.isInitializing.get()) {
          filterParams.init = true;
        }

        // if page > 1 then we have to query for enough records
        if (pageNum > 1) {
          filterParams.minInitialRecords = recordList.getPageSize() * (pageNum + 1);
        }

        data = await fetchRecords({ filterParams });
      }

      // if init, process stats
      if (state.status.isInitializing.get()) {
        state.status.isInitializing.set(false);
        state.status.stats.recordCount.set(data?.stats?.recordCount || 0);
        state.status.stats.incompleteCount.set(data?.stats?.incompleteCount || 0);
        state.status.stats.rejectedCount.set(data?.stats?.rejectedCount || 0);
        state.status.stats.outdatedCount.set(data?.stats?.outdatedCount || 0);
        state.status.stats.lateCompletionCount.set(data?.stats?.lateCompletionCount || 0);
        state.status.stats.notReviewedCount.set(data?.stats?.notReviewedCount || 0);
        state.status.stats.deploymentTrackMapCount.set(data?.stats?.deploymentTrackMapCount || 0);
        state.status.stats.overdueVaccinationCount.set(data?.stats?.overdueVaccinationCount || 0);
        state.status.stats.hasLiveDeploymentTracks.set(data?.stats?.hasLiveDeploymentTracks || false);
      }

      state.calendar.isLoading.set(false);
      state.status.isLoading.set(false);
    }

    const loadMore = recordList.setPageNum(pageNum);

    if (loadMore) {
      filterParams = { ...state.status.filterParams.get() };
      filterParams.chunkEndDate = state.status.chunkStartDate.get();
      fetchRecords({ filterParams });
    }

    return data;
  }


  /**
   * return the record handler for a given type
   */
  const recordHandler = (type) => {
    const map = {
      [Types.Record.EVENT]: trainingHandler,
      [Types.Record.EXERCISE]: trainingHandler,
      [Types.Record.ANSWER]: trainingHandler,
      [Types.Record.DEPLOYMENT]: deploymentHandler,
      [Types.Record.CLASS]: classHandler,
      [Types.Record.VET_VISIT]: vetVisitHandler,
    }
    return map[type.toString()];
  }


  /**
   * fetches single record via type & id
   */
  const fetchRecord = async (type, id) => {
    // prevent api request for 0 id
    if (id === 0 || id === "0") {
      return;
    }

    const handler = recordHandler(type);
    const route = handler.recordRoute(type, id);
    const index = handler.recordIndex(type, state);

    // prevent duplicate simultaneous requests
    if (!state.fetching[route].ornull) {
      state.fetching[route].set(true);

      const response = await axios.get(route);
      const record = response.data;
      logger.log(`[record-store.js] Fetched new record for route ${route}`);

      if (!record) {
        index[id].set(false);
      } else {
        storeRecord(record, state);
      }

      state.fetching[route].set(none);

      return record;
    }
  }


  /**
   * fetches multiple records based on filter params
   */
  const fetchRecords = async ({
    filterParams,
    resetRecordList = false,
    noListProcessing = false
  }) => {
    if (state.status.isBatchFetching.get()) {
      return;
    }

    state.status.isBatchFetching.set(true);

    // always add calendar chunk start date so back-end knows if it needs to return more cal data
    filterParams.calendarChunkEndDate = state.calendar.chunkStartDate.get();

    // format start date
    if (filterParams.startDate) {
      filterParams.startDate = formatISO(parseISO(filterParams.startDate));
    }

    // inclusive end date
    if (filterParams.endDate) {
      filterParams.endDate = formatISO(endOfDay(parseISO(filterParams.endDate)));
    }

    const startTime = performance.now();
    const response = await axios.get('/records/search', { params: objectToUrlParams(filterParams) });
    const data = response.data;

    logger.log('[record-store.js] %d event, deployment, training class and vet visit items retrieved in %dms',
      data ? data.records.length : 0, performance.now() - startTime);

    // process initial calendar data
    if (data?.calendarRecords) {
      state.calendar.chunkStartDate.set(data.calendarChunkStartDate);
      data.calendarRecords.forEach(record => {
        updateDateIndexRecord(record, state);
      });
    }
    // NOTE: triggering record list reset assumes you want to keep the same page.
    // -> false is passed to reset
    if (resetRecordList) {
      recordList.reset(false);
    }
    processRecords(data, state, noListProcessing);

    state.status.isBatchFetching.set(false);

    return data;
  }


  /**
   * fetch specific records provided in id lists.
   * this is used for batch query of records when an uncached date is clicked
   */
  const fetchRecordsList = async (eventIds, deploymentIds, classIds) => {
    if ((!eventIds || eventIds.length === 0)
      && (!deploymentIds || deploymentIds.length === 0)
      && (!classIds || classIds.length === 0)) return;

    const processListRecords = (records, s) => {
      records.forEach(record => {
        storeRecord(record, s);
      });
    }

    const params = new URLSearchParams({
      eventList: eventIds,
      deploymentList: deploymentIds,
      classList: classIds
    });

    const response = await axios.get(`/records/get-list`, { params });
    const data = response.data;

    processListRecords(data.events, state);
    processListRecords(data.deployments, state);
    processListRecords(data.classes, state);
  }


  /**
   * processes record query result
   */
  const processRecords = (data, s, noListProcessing = false) => {
    if (data) {
      s.status.chunkStartDate.set(data.chunkStartDate);

      // we process data.records into the store indexes as this is the list of complete record data
      data.records.forEach(record => {
        storeRecord(record, s);
      });

      // when refreshing a date-selected page we init by fetching records to fill cache.
      // we don't want these results processed into the list, which would trigger a display of records
      // that don't satisfy the date filter.
      if (!noListProcessing) {
        // we send data.results to the record list as this is the list of records that satisfy the search query
        recordList.processChunk(data.results, data.numTotal ?? null);
      }

      logger.log(`[record-store.js] Store: ${Object.keys(s.deployment).length}dep|${Object.keys(s.training.event).length}ev|`
        + `${Object.keys(s.training.exercise).length}ex|${Object.keys(s.training.answer).length}ans.`, s);
    }
  }


  /**
   * stores a single record using the record type's processor
   */
  const storeRecord = (record, state) => {
    recordHandler(record.recordType).processRecord(record, updateDateIndexRecord, state, user);
  }


  /**
   * refreshes records
   */
  const refreshRecords = async (setLoadingStatus) => {
    // prevent refresh if already loading
    if (state.status.isLoading.get()) {
      return;
    }

    if (setLoadingStatus) {
      state.status.isLoading.set(true);
    }

    let filterParams = { ...state.status.filterParams.get() };
    filterParams.minInitialRecords = (recordList.getPageNum() + numExtraPagesBuffer) * recordList.getPageSize();

    await fetchRecords({
      filterParams,
      resetRecordList: true,
      noListProcessing: Boolean(filterParams.date)
    });

    if (filterParams.date) {
      const dateState = state.calendar.date[filterParams.date].ornull;
      const records = dateState ? dateState.records.get() : [];
      recordList.processChunk(records, undefined);
    }

    if (state.status.isLoading.get()) {
      state.status.isLoading.set(false);
    }
  }


  /**
   * refreshes live tracking (supervisor only)
   */
  const updateLiveTrackStatus = async () => {
    // when live map dialog is open it does its own interval data querying and updates the 
    // 'hasLiveDeploymentTracks' value there. so we do a check here to avoid double-querying.
    if (!user.role.isSupervisor || state.status.stats.isLiveMapDialogOpen.get()) {
      return;
    }
    
    const response = await axios.get('/deployment/has-live-tracks');
    if (response.status === 200) {
      if (response.data !== state.status.stats.hasLiveDeploymentTracks.get()) {
        state.status.stats.hasLiveDeploymentTracks.set(response.data);
      }
    }
  }


  // ---------------------------------------------------------------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------
  // CALENDAR / DATE INDEX FUNCTIONS
  // ---------------------------------------------------------------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------


  /**
   * gets next calendar chunk
   */
  const fetchNextCalendarChunk = async () => {
    if (state.calendar.isFetching.get()) return;

    state.calendar.isFetching.set(true);
    const handlers = state.status.filterParams.handlers.get() || [];
    const calStartDate = formatISO(addMonths(parseISO(state.calendar.chunkStartDate.get()), -calendarMonthBuffer));
    const calEndDate = state.calendar.chunkStartDate.get();
    const response = await axios.get('/records/calendar', { params: objectToUrlParams({ handlers, calStartDate, calEndDate }) });

    if (response.status === 200) {
      const data = response.data;
      state.calendar.chunkStartDate.set(calStartDate);
      state.calendar.isFetching.set(false);
      data.forEach(record => {
        updateDateIndexRecord(record, state);
      });
    }
  }

  
/**
 * creates all default date states for a given month
 */
  const createDateStates = (monthDate) => {
    eachDayOfInterval({
      start: monthDate,
      end: endOfMonth(monthDate)
    }).forEach(date => {
      const key = format(date, dateParamFormat);
      if (!state.calendar.date[key].ornull) {
        state.calendar.date[key].merge({
          ...emptyDateRecord,
          selected: state.calendar.selectedDates.get().includes(key)
        });
      }
    });

  }


  /**
   * sets given list of dates to 'selected'.
   * DO NOT BATCH! this triggers render cycles for all calendar dates if batched. wtfbro.
   */
  const setSelectedDates = (dateKeys) => {
    state.calendar.selectedDates.get().forEach(dateKey => {
      state.calendar.date[dateKey].set(p => ({ ...emptyDateRecord, ...p, selected: false }));
    });

    dateKeys.forEach(dateKey => {
      state.calendar.date[dateKey].set(p => ({ ...emptyDateRecord, ...p, selected: true }));
    });

    // save selected
    state.calendar.selectedDates.set(dateKeys);
  }


  /**
   * sets 'selected' for dates from unprocessed list of dates
   */
  const setSelectedDatesFromList = (dateList) => {
    let dateKeys = {};
    dateList.forEach(date => {
      const dateKey = date.split(' ')[0];
      dateKeys[dateKey] = true;
    });

    const dates = Object.keys(dateKeys);
    if (dates.length > 0) {
      setSelectedDates(dates);
    }
  }


  /**
   * inserts or updates a record in the date index
   */
  const updateDateIndexRecord = (record, s) => {
    if (record.id === 0) {
      return;
    }

    const dateKey = format(parseISO(record.date), dateParamFormat);

    if (!s.calendar.date[dateKey].ornull) {
      // s.date[dateKey].set({ selected: false, records: [] });
      s.calendar.date[dateKey].set({ ...emptyDateRecord });
    }

    s.calendar.date[dateKey].records.set(p => {

      if (p.find(dateRecord =>
        dateRecord.recordType === record.recordType
        && dateRecord.id === record.id
      )) {
        // replace existing record in list
        return p.map(dateRecord =>
          dateRecord.recordType === record.recordType
            && dateRecord.id === record.id
            ? record
            : dateRecord);
      } else {
        // add new record to list
        return [
          ...p,
          record
        ];
      }

    });
  }


  /**
   * removes a record from the date index
   */
  const removeDateIndexRecord = (record, s = state, date = null) => {
    if (!record) {
      return;
    }
    
    let dateKey = format(parseISO(date ?? record.date), dateParamFormat);

    if (!!s.calendar.date[dateKey].ornull) {
      s.calendar.date[dateKey].records.set(prev =>
        prev.filter(r => !(r.id === record.id && r.recordType === record.recordType)));
    }
  }


  const processRecordDateChange = (substate, recordId, previousDate) => {
    // if the record date was updated, update the date index by removing the record from the previous date.
    if (previousDate && substate[recordId]) {
      const record = substate[recordId].get();
      const newDate = format(parseISO(record.date), dateParamFormat);
      const oldDate = format(parseISO(previousDate), dateParamFormat);
      if (newDate !== oldDate) {
        removeDateIndexRecord(record, state, previousDate);
        const filterParams = state.status.filterParams.get();
        if (filterParams.date === oldDate) {
          // refresh the view to remove the record whose date changed
          setTimeout(() => setFilter(filterParams, recordList.getPageNum(), true));
        }
      }
    }     
  }


  // ---------------------------------------------------------------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------
  // INDIVIDUAL RECORD INDEX FUNCTIONS
  // ---------------------------------------------------------------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------


  /**
   * returns record state
   */
  const useRecord = (type, id) => {
    if (type === undefined || id === undefined || id === null || isNaN(id) || id < 0) {
      // return empty state on input error
      return hookstate();
    }

    const index = recordHandler(type).recordIndex(type, state);

    if (!index[id].ornull) {
      (async () => {
        fetchRecord(type, id);
      })();
    } else if (index[id].get() === false) {
      // record not found -> return empty state
      return hookstate();
    }

    return index[id];
  };


  /**
   * returns array of record states
   * i tried to just return useRecord but it didn't like this so i had to duplicate the code
   */
  const useRecordArray = (type, ids) => {
    if (!type || !ids) return hookstate();

    let list = ids.map(id => {
      if (type === undefined || id === undefined || id === null || isNaN(id) || id < 0) {
        // return empty state on input error
        return hookstate();
      }

      const index = recordHandler(type).recordIndex(type, state);

      if (!index[id].ornull) {
        (async () => {
          fetchRecord(type, id);
        })();
      } else if (index[id].get() === false) {
        // record not found -> return empty state
        return hookstate();
      }

      return index[id];
    });

    // filter out empty items that may not have been retrieved just yet
    return list.filter(a => Boolean(a.get()));
  }


  /**
   * gets async record
   */
  const getRecord = async (type, id) => {
    if (type === undefined || id === undefined || id === null || isNaN(id) || id < 0) {
      return;
    }

    const index = recordHandler(type).recordIndex(type, state);

    if (!index[id].ornull) {
      await fetchRecord(type, id);
    } else if (index[id].get() === false) {
      // record not found
      return null;
    }

    return index[id].get();
  };



  // ---------------------------------------------------------------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------
  // RECORD SAVING / DUPLICATING / DELETING FUNCTIONS
  // ---------------------------------------------------------------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------


  /**
   * checks if current filter is a config that is known to be continuous.
   * NOTE: there are plenty of more complex filters that could be continuous
   * but we aren't checking for them. this is just a high-level check intended
   * to reduce processing in common known scenarios.
   */
  const isFilterContinuous = () => {
    const filterParams = state.status.filterParams.get();
    const numParams = Object.keys(filterParams).length;
    return numParams === 0
      || filterParams.date
      || (filterParams.recordGroup === Types.RecordGroup.TRAINING && numParams === 1)
      || (filterParams.recordGroup === Types.RecordGroup.DEPLOYMENT && numParams === 1)
  }


  /**
   * checks if filter is all or one group or date to determine what kind of update the record list needs.
   * returns true if a full refresh is not required so caller can manually update record list.
   * returns false and performs full refresh if the filter is complex.
   */
  const doUpdateRecordListManually = async () => {
    if (isFilterContinuous()) {
      return true;
    } else {
      // refresh record list up to current page plus
      const filterParams = { ...state.status.filterParams.get() };
      filterParams.minInitialRecords = (recordList.getPageNum() + numExtraPagesBuffer) * recordList.getPageSize();
      await fetchRecords({
        filterParams,
        resetRecordList: true
      });
      return false;
    }
  }


  /**
   * called after any record(s) are updated
   */
  const onRecordsUpdated = (data) => {
    if (data.recordCount !== undefined) {
      state.status.stats.recordCount.set(data.recordCount);
    }
    if (data.incompleteCount !== undefined) {
      state.status.stats.incompleteCount.set(data.incompleteCount);
    }
    if (data.rejectedCount !== undefined) {
      state.status.stats.rejectedCount.set(data.rejectedCount);
    }
    if (data.outdatedCount !== undefined) {
      state.status.stats.outdatedCount.set(data.outdatedCount);
    }
    if (data.lateCompletionCount !== undefined) {
      state.status.stats.lateCompletionCount.set(data.lateCompletionCount);
    }
    if (data.notReviewedCount !== undefined) {
      state.status.stats.notReviewedCount.set(data.notReviewedCount);
    }
    if (data.overdueVaccinationCount !== undefined) {
      state.status.stats.overdueVaccinationCount.set(data.overdueVaccinationCount);
    }
    if (data.deploymentTrackMapCount !== undefined) {
      state.status.stats.deploymentTrackMapCount.set(data.deploymentTrackMapCount);
    }

    if (!isFilterContinuous()) {
      recordList.setPossiblyOutdated(true);
    }
  }


  /**
   * saves event and returns event record state
   */
  const saveEvent = async (id, event, previousDate = null) => {
    const method = id === 0 ? 'post' : 'put';
    const url = `/training/event${id > 0 ? `/${id}` : ''}`;
    const response = await axios({
      method,
      url,
      data: event,
    });

    if (response.isSuccess) {
      analytics.trackGA4('event', { name: 'record_save', params: { record_type: 'event', save_type: id === 0 ? 'added' : 'edited' } });

      const data = response.data;
      const trainingItem = data.trainingItem;
      storeRecord(trainingItem, state);

      if (await doUpdateRecordListManually()) {
        recordList.update([trainingItem]);
      }

      processRecordDateChange(state.training.event, trainingItem.id, previousDate);
      onRecordsUpdated(data);

      return { response, record: state.training.event[trainingItem.id] };
    } else {
      return { response, record: null };
    }
  }


  /**
   * saves event and returns event record state
   */
  const saveQuickTrainingEvent = async (event) => {
    const formData = objectToFormData(event, '', []);

    const response = await axios({
      method: 'POST',
      url: '/training/event/quick',
      data: formData,
    });

    if (response.isSuccess) {      
      // analytics
      const exercise = event.exercises[0];
      const answer = exercise.answers?.[0];
      const workmode_type = Types.WorkMode.getName(t, exercise.workMode);
      const patrol_type = getPatrolSummary(Types.Record.EXERCISE, exercise.patrol?.patrolTypes, t);

      analytics.trackGA4('event', { name: 'record_save', params: { record_type: 'event', save_type: 'added_quick' } });
      analytics.trackGA4('event', {
        name: 'record_save',
        params: {
          record_type: 'exercise',
          save_type: 'added_quick',
          workmode_type,
          patrol_type
        }
      });
      
      if (answer) {
        analytics.trackGA4('event', {
          name: 'record_save',
          params: {
            record_type: 'completion',
            save_type: 'added_quick',
            workmode_type,
            patrol_type,
          }
        });        
      }

      // store record
      const data = response.data;
      const trainingItem = data.trainingItem;
      storeRecord(trainingItem, state);

      if (await doUpdateRecordListManually()) {
        recordList.update([trainingItem]);
      }

      onRecordsUpdated(data);

      return { response, record: state.training.event[trainingItem.id] };
    } else {
      return { response, record: null };
    }
  }


  /**
   * saves exercise and returns exercise record state
   */
  const saveExercise = async (id, exercise) => {
    const response = await axios({
      method: id === 0 ? 'post' : 'put',
      url: `/training/exercise${id > 0 ? `/${id}` : ''}`,
      data: exercise,
    });

    if (response.isSuccess) {
      analytics.trackGA4('event', {
        name: 'record_save',
        params: {
          record_type: 'exercise',
          save_type: id === 0 ? 'added' : 'edited',
          workmode_type: Types.WorkMode.getName(t, exercise.workMode),
          patrol_type: getPatrolSummary(Types.Record.EXERCISE, exercise.patrol?.patrolTypes, t)
        }
      });

      // outdated analytics
      if (id > 0) {
        const currExer = state.training.exercise[id].get();
        const currEvent = state.training.event[currExer.eventId].get();

        // check if the update caused any outdated
        if (currExer.fullStats.updated > 0) {
          analytics.trackGA4('event', {
            name: 'record_outdate',
            params: {
              workmode_type: Types.WorkMode.getName(t, exercise.workMode),
              group_size_type: getSizeTypeAnalyticsLabel(currEvent.members.length),
              outdated_count_type: getSizeTypeAnalyticsLabel(currExer.fullStats.updated)
            }
          });
        }
      }

      const data = response.data;
      const trainingItem = data.trainingItem;

      // exercise saving has repeatedly struggled at unpredictable times based on whether the save
      // is batched or not. every time there's an issue it seems to be fixed when toggling whether
      // the save is batched or not. 
      // previously, batching needed to happen because of occasional long hang times when saving
      // new exercises.
      // currently, batching needs to be removed because of occasional long hang times when saving
      // existing scenario exercises.
      // i think i will try using batching only for new exers.
      
      storeRecord(trainingItem, state);

      if (await doUpdateRecordListManually()) {
        recordList.update([trainingItem]);
      }

      onRecordsUpdated(data);
      return { response, record: state.training.exercise[data.exerciseId] };
    } else {
      return { response, record: null };
    }
  }


  /**
   * saves answer and returns answer record state
   */
  const saveAnswer = async (id, answer, dateCompleted, skipAnalytics = false) => {
    const formData = objectToFormData(answer, '', []);

    const url = `/training/answer/${id}`;
    const response = await axios.put(url, formData);

    if (response.isSuccess) {
      const data = response.data;
      const exerId = state.training.answer[id].get().exerciseId;
      const exer = data.trainingItem.exercises.find(ex => ex.id === exerId);
      const currAnswer = state.training.answer[id].get();
      const currExer = state.training.exercise[exer.id].get();
      const currEvent = state.training.event[exer.eventId].get();

      if (!skipAnalytics) {
        // creation analytics
        analytics.trackGA4('event', {
          name: 'record_save',
          params: {
            record_type: 'completion',
            save_type: dateCompleted === null ? 'added' : 'edited',
            workmode_type: Types.WorkMode.getName(t, exer.workMode),
            patrol_type: getPatrolSummary(Types.Record.EXERCISE, exer.patrol.patrolTypes, t)
          }
        });

        // outdated analytics
        if (currAnswer.isOutdated) {
          analytics.trackGA4('event', {
            name: 'record_verify',
            params: {
              workmode_type: Types.WorkMode.getName(t, exer.workMode),
              group_size_type: getSizeTypeAnalyticsLabel(currEvent.members.length),
              outdated_count_type: getSizeTypeAnalyticsLabel(currExer.fullStats.outdated)
            }
          });
        }
      }

      const trainingItem = data.trainingItem;
      storeRecord(trainingItem, state);
      onRecordsUpdated(data);
      return { response, record: state.training.answer[data.answerId] };
    } else {
      return { response, record: null };
    }
  }


  /**
   * Updates the training exercise's event and returns the exercise record state
   */
  const changeExerciseEvent = async (exerciseId, eventId) => {
    const response = await axios({
      method: 'post',
      url: '/training/exercise/changeEvent',
      data: { exerciseId, targetEventId: eventId },
    });

    if (response.isSuccess) {
      analytics.trackGA4('event', { name: 'record_movetoevent', params: { record_type: 'exercise' } });

      const data = response.data;
      const updatedEvent = data.trainingItem;
      const originalEvent = data.originalTrainingItem;

      // batching these state updates will freeze the screen for some reason
      storeRecord(updatedEvent, state);
      storeRecord(originalEvent, state);


      if (await doUpdateRecordListManually()) {
        recordList.update([updatedEvent, originalEvent]);
      }

      onRecordsUpdated(data);
      return { response, record: state.training.exercise[data.exerciseId] };
    } else {
      return { response, record: null };
    }
  }


  /**
   * Duplicates the event and returns the new event record state
   */
  const duplicateEvent = async (eventId) => {
    const response = await axios({
      method: 'post',
      url: `/training/event/${eventId}/duplicate`,
    });

    if (response.isSuccess) {
      analytics.trackGA4('event', { name: 'record_duplicate', params: { record_type: 'event' } });

      const data = response.data;
      const trainingItem = data.trainingItem;

      storeRecord(trainingItem, state);

      if (await doUpdateRecordListManually()) {
        recordList.add([trainingItem]);
      }

      onRecordsUpdated(data);
      return { response, record: state.training.event[trainingItem.id] };
    } else {
      return { response, record: null };
    }
  }


  /**
   * Duplicates the exercise and returns the new exercise record state
   */
  const duplicateExercise = async (exerciseId) => {
    const response = await axios({
      method: 'post',
      url: `/training/exercise/${exerciseId}/duplicate`,
    });

    if (response.isSuccess) {
      analytics.trackGA4('event', { name: 'record_duplicate', params: { record_type: 'exercise' } });

      const data = response.data;
      const trainingItem = data.trainingItem;

      storeRecord(trainingItem, state);

      if (await doUpdateRecordListManually()) {
        recordList.update([trainingItem]);
      }

      onRecordsUpdated(data);
      return { response, record: state.training.exercise[data.exerciseId] };
    } else {
      return { response, record: null };
    }
  }


  /**
   * Deletes the exercise and removes it from the all records list state
   */
  const deleteExercise = async (exerciseId) => {
    const response = await axios({
      method: 'delete',
      url: `/training/exercise/${exerciseId}`,
    });

    if (response.isSuccess) {
      const data = response.data;

      analytics.trackGA4('event', { name: 'record_delete', params: { record_type: 'exercise' } });

      const trainingItem = data.trainingItem;
      storeRecord(trainingItem, state);

      if (await doUpdateRecordListManually()) {
        recordList.update([trainingItem]);
      }

      onRecordsUpdated(data);
      setTimeout(() => state.training.exercise[exerciseId].set(none), 2000);
    } 

    return { response };
  }


  /**
   * Deletes the event and removes it from the all records list state
   */
  const deleteEvent = async (eventId) => {
    const response = await axios({
      method: 'delete',
      url: `/training/event/${eventId}`,
    });

    if (response.isSuccess) {
      analytics.trackGA4('event', { name: 'record_delete', params: { record_type: 'event' } });

      const data = response.data;
      const deletedEvent = state.training.event[eventId].get();
      recordList.remove([deletedEvent]);
      removeDateIndexRecord(deletedEvent);
      onRecordsUpdated(data);
      setTimeout(() => state.training.event[eventId].set(none), 2000);
    } 

    return { response };
  }


  /**
   * Deletes the completed answers for the training exercise
   */
  const deleteCompletedAnswers = async (exerciseId, answerIds) => {
    const response = await axios({
      method: 'delete',
      url: 'training/answer/completed',
      data: { exerciseId, answerIds },
    });

    if (response.isSuccess) {
      analytics.trackGA4('event', { name: 'record_delete', params: { record_type: 'completion' } });

      const data = response.data;
      const trainingItem = data.trainingItem;
      onRecordsUpdated(data);
      storeRecord(trainingItem, state);
    }

    return { response };
  }


  /**
   * saves deployment and returns updated state object
   */
  const saveDeployment = async (id, deployment, previousDate = null, skipAnalytics = false) => {
    const method = id === 0 ? 'post' : 'put';
    const url = `/deployment${id > 0 ? `/${id}` : ''}`;
    const response = await axios({
      method,
      url,
      data: deployment,
    });

    if (response.isSuccess) {
      const data = response.data;
      const newDeploy = data.deployment;

      const preSaveDeployHasComments = state.deployment[id]?.get()?.summary?.comments?.length > 0;
      const postSaveDeployHasComments = newDeploy.summary?.comments?.length > 0;
      const isAdd = !preSaveDeployHasComments && postSaveDeployHasComments;
      const isEdit = preSaveDeployHasComments;

      if (!skipAnalytics && (isAdd || isEdit)) {
        analytics.trackGA4('event', {
          name: 'record_save',
          params: {
            record_type: 'deployment',
            save_type: isAdd ? 'added' : isEdit ? 'edited' : '',
            workmode_type: Types.WorkMode.getName(t, newDeploy.workMode),
            patrol_type: getPatrolSummary(Types.Record.DEPLOYMENT, newDeploy.patrol?.patrolTypes, t)
          }
        });
      }

      storeRecord(newDeploy, state);

      if (await doUpdateRecordListManually()) {
        recordList.update([newDeploy]);
      }

      processRecordDateChange(state.deployment, newDeploy.id, previousDate);
      onRecordsUpdated(data);
      
      return { response, record: state.deployment[newDeploy.id] };
    } else {
      return { response, record: null };
    }
  }


  /**
   * Deletes the deployment record and removes it from the all records list state
   */
  const deleteDeployment = async (deploymentId) => {
    const response = await axios({
      method: 'delete',
      url: `/deployment/${deploymentId}`,
    });

    if (response.isSuccess) {
      analytics.trackGA4('event', { name: 'record_delete', params: { record_type: 'deployment' } });
      const data = response.data;
      const deletedDeployment = state.deployment[deploymentId].get();
      recordList.remove([deletedDeployment]);
      removeDateIndexRecord(deletedDeployment);
      onRecordsUpdated(data);
      setTimeout(() => state.deployment[deploymentId].set(none), 2000);
    } 

    return { response };
  }


  /**
   * saves class and returns class record state
   */
  const saveClass = async (id, trainingClass, previousDate = null) => {
    const formData = objectToFormData(trainingClass, '', []);

    const method = id === 0 ? 'post' : 'put';
    const url = `/class${id > 0 ? `/${id}` : ''}`;
    const response = await axios({
      method: method,
      url: url,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' }
    }
    );

    if (response.isSuccess) {
      const data = response.data;
      const trainClass = data.trainingItem;
      analytics.trackGA4('event', { name: 'record_save', params: { record_type: 'class', save_type: id === 0 ? 'added' : 'edited' } });
      storeRecord(trainClass, state);

      if (await doUpdateRecordListManually()) {
        recordList.update([trainClass]);
      }

      processRecordDateChange(state.trainingClass, trainClass.id, previousDate);
      onRecordsUpdated(data);

      return { response, record: state.trainingClass[trainClass.id] };
    } else {
      return { response, record: null };
    }
  }


  /**
   * Deletes the class record and removes it from the all records list state
   */
  const deleteClass = async (classId) => {
    const response = await axios({
      method: 'delete',
      url: `/class/${classId}`,
    });

    if (response.isSuccess) {
      analytics.trackGA4('event', { name: 'record_delete', params: { record_type: 'class' } });

      const data = response.data;
      const deletedClass = state.trainingClass[classId].get();
      recordList.remove([deletedClass]);
      removeDateIndexRecord(deletedClass);
      onRecordsUpdated(data);
      setTimeout(() => state.trainingClass[classId].set(none), 2000);
    } 

    return { response };
  }


  /**
   * saves vet visit and returns record state
   */
  const saveVetVisit = async (id, vetVisit, previousDate = null) => {
    const formData = objectToFormData(vetVisit, '', []);

    const method = id === 0 ? 'post' : 'put';
    const url = `/vet${id > 0 ? `/${id}` : ''}`;
    const response = await axios({
      method: method,
      url: url,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' }
    }
    );

    if (response.isSuccess) {
      const data = response.data;
      const vetVisit = data.vetVisitItem;
      analytics.trackGA4('event', { name: 'record_save', params: { record_type: 'vet_visit', save_type: id === 0 ? 'added' : 'edited' } });
      storeRecord(vetVisit, state);

      if (await doUpdateRecordListManually()) {
        recordList.update([vetVisit]);
      }

      processRecordDateChange(state.vetVisit, vetVisit.id, previousDate);
      onRecordsUpdated(data);

      return { response, record: state.vetVisit[vetVisit.id] };
    } else {
      return { response, record: null };
    }
  }


  /**
   * Deletes the vet visit record and removes it from the all records list state
   */
  const deleteVetVisit = async (vetVisitId) => {
    const response = await axios({
      method: 'delete',
      url: `/vet/${vetVisitId}`,
    });

    if (response.isSuccess) {
      analytics.trackGA4('event', { name: 'record_delete', params: { record_type: 'vet_visit' } });

      const data = response.data;
      const deletedVetVisit = state.vetVisit[vetVisitId].get();
      recordList.remove([deletedVetVisit]);
      removeDateIndexRecord(deletedVetVisit);
      onRecordsUpdated(data);
      setTimeout(() => state.vetVisit[vetVisitId].set(none), 2000);
    } 

    return { response };
  }


  /**
   * sets event attendance
   */
  const setAttendance = async (isAttending, eventId) => {
    const response = await axios.post(`/training/event/${eventId}/attendance`, { isAttending });

    if (response.status === 200) {
      const attendType = isAttending ? "attending" : "not_attending";
      analytics.trackGA4('event', { name: 'record_attend', params: { record_type: 'event', attend_type: attendType } });

      const data = response.data;
      const trainingItem = data.trainingItem;

      // if no training item then we are not attending and event is over -> remove from list
      if (!trainingItem) {
        const deletedEvent = state.training.event[eventId];
        // bit of a hack: set event membership state for visual feedback just before it's removed
        deletedEvent.membership.isAttending.set(false);
        setTimeout(() => {
          recordList.remove([deletedEvent.get()]);
          removeDateIndexRecord(deletedEvent.get());
          onRecordsUpdated(data);
          setTimeout(() => state.training.event[eventId].set(none), 2000);
        }, 600);
      } else {
        onRecordsUpdated(data);
        storeRecord(trainingItem, state);
      }
    }
  }


  /**
   * sets record review status
   */
  const setReviewStatus = async (type, id, status, reason) => {
    const reviewType = Types.ReviewStatus.getName(t, status);
    const recordType = Types.Record.getName(t, type);
    if (type === Types.Record.ANSWER) {
      const response = await axios.post(`/training/answer/${id}/review-status`, { status, reason });
      if (response.status === 200) {
        const data = response.data;
        const exerciseId = state.training.answer[id].exerciseId.get();
        state.training.answer[id].review.set(data.reviewModel);
        state.training.exercise[exerciseId].reviewStats.set(data.exerciseReviewStats);
        state.training.exercise[exerciseId].managedHandlers.set(data.exerciseManagedHandlers);
        onRecordsUpdated(data);
      }
    } else if (type === Types.Record.DEPLOYMENT) {
      const response = await axios.post(`/deployment/${id}/review-status`, { status, reason });
      if (response.status === 200) {
        const data = response.data;
        state.deployment[id].review.set(data.reviewModel);
        onRecordsUpdated(data);
      }
    } else if (type === Types.Record.CLASS) {
      const response = await axios.post(`/class/${id}/review-status`, { status, reason });
      if (response.status === 200) {
        const data = response.data;
        state.trainingClass[id].review.set(data.reviewModel);
        onRecordsUpdated(data);
      }
    }

    analytics.trackGA4('event', { name: 'record_review', params: { record_type: recordType, review_type: reviewType } });
  }


  /**
   * sets trainer comments for answer
   */
  const setTrainerComments = async (answerId, comments) => {
    const response = await axios.post(`/training/answer/${answerId}/trainer-comments`, { comments });
    if (response.status === 200) {
      const data = response.data;
      state.training.answer[answerId].trainerComments.set(data.trainerComments);
    }

    // analytics.trackGA4('event', { name: 'record_review', params: { record_type: recordType, review_type: reviewType } });
  }


  /**
   * creates deployment and track for tracking 
   */
  const createDeploymentTrack = async (model) => {
    const response = await axios.post('/deployment/track', model);
    if (response.status === 200) {

      const data = response.data;
      const track = data.track;
      const deployment = data.deployment;

      storeRecord(deployment, state);

      if (await doUpdateRecordListManually()) {
        recordList.update([deployment]);
      }

      // manually update counts since this record status is known
      state.status.stats.recordCount.set(state.status.stats.recordCount.get() + 1);
      state.status.stats.incompleteCount.set(state.status.stats.incompleteCount.get() + 1);

      // combine data into single track object
      track.deployment = deployment;
      track.createdRecord = deployment;

      return { response, record: state.deployment[deployment.id], track };
    }

    return  { response, record: null, track: null };
  }


  /**
   * creates lead track for tracking
   */
  const createLeadTrack = async (model) => {
    const response = await axios.post('/training/exercise/track', model);
    if (response.status === 200) {

      // combine data into single track object
      const track = response.data.track;

      // we don't have an associated record for this
      return { response, record: null, track };
    }

    return { response, record: null, track: null };
  }


  /**
   * creates follow track and possible event/exercise for tracking
   */
  const createFollowTrack = async (model) => {
    const findCompletionByTrackingId = (exercises, trackingId) => {
      if (exercises.length === 0) {
        return false;
      } else {
        return exercises
          .filter(e => e.workMode === Types.WorkMode.PATROL)
          .flatMap(e => e.answers)
          .find(c => c.patrol.patrolTypes.find(pt => pt.tracking?.id === trackingId));
      }
    } 

    // prevent 409/410 responses from being auto-handled
    const response = await axios.post('/training/answer/track', model, { 
      onError: (e) => e.response?.status === 409 || e.response?.status === 410
    });
    if (response.isSuccess) {
      // TODO: analytics

      const data = response.data;
      const track = data.track;
      const trainingItem = data.trainingItem;
      const createdRecord = data.createdRecord;
      
      if (trainingItem) {
        storeRecord(trainingItem, state);
        if (await doUpdateRecordListManually()) {
          recordList.update([trainingItem]);
        }
      }

      if (createdRecord.type !== 'completion-tracking-record') {
        // manually update counts since existence of a created record (event or exercise) means an incomplete exercise was created
        state.status.stats.recordCount.set(state.status.stats.recordCount.get() + 1);
        state.status.stats.incompleteCount.set(state.status.stats.incompleteCount.get() + 1);
      }

      // find the relevant completion and combine data into single track object
      const completionTrackingId = track.completionTrackingId;
      const completion = findCompletionByTrackingId(trainingItem.exercises, completionTrackingId);
      track.completion = completion;
      track.createdRecord = createdRecord;

      return { response, record: completion, track };
    }

    return { response, record: null, track: null };
  }




  // ---------------------------------------------------------------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------
  // RECORD STORE INTERFACE
  // ---------------------------------------------------------------------------------------------------------
  // ---------------------------------------------------------------------------------------------------------

  return ({

    // -------------------------------
    // general functionality
    // -------------------------------

    setFilter: async (filterParams, pageNum) => {
      return setFilter(filterParams, pageNum);
    },

    refreshRecords: async (setIsLoadingStatus = false) => {
      return refreshRecords(setIsLoadingStatus);
    },

    reset: () => {
      reset();
    },

    useStatus: () => {
      return state.status;
    },

    useRecord: (type, id) => {
      return useRecord(type, id);
    },

    useRecordArray: (type, ids) => {
      return useRecordArray(type, ids);
    },

    getRecord: (type, id) => {
      return getRecord(type, id);
    },

    updateRecord: (record) => {
      storeRecord(record, state);
      const index = recordHandler(record.recordType).recordIndex(record.recordType, state);
      return index[record.id];
    },

    setReviewStatus: (type, id, status, reason) => {
      return setReviewStatus(type, id, status, reason);
    },


    // -------------------------------
    // live map functions
    // -------------------------------

    updateLiveTrackStatus: async () => {
      return updateLiveTrackStatus();
    },

    setHasLiveTracks: (val) => {
      state.status.stats.hasLiveDeploymentTracks.set(val);
    },

    setIsLiveMapDialogOpen: (val) => {
      state.status.stats.isLiveMapDialogOpen.set(val);
    },


    // -------------------------------
    // calendar functions
    // -------------------------------

    useDate: (date) => {
      if (state.status.isInitializing.get()) {
        return hookstate();
      }

      return state.calendar.date[format(date, dateParamFormat)];
    },

    useCalendarStatus: () => {
      return {
        chunkStartDate: state.calendar.chunkStartDate,
        isLoading: state.calendar.isLoading,
      };
    },

    fetchNextCalendarChunk: () => {
      return fetchNextCalendarChunk();
    },

    createDateStates: (monthDate) => {
      createDateStates(monthDate);
    },

    setSelectedDates: (dateList) => {
      setSelectedDatesFromList(dateList);
    },


    // -------------------------------
    // training functions
    // -------------------------------

    saveEvent: (id, event, prevDate) => {
      return saveEvent(id, event, prevDate);
    },

    saveQuickTrainingEvent: (event) => {
      return saveQuickTrainingEvent(event);
    },

    saveExercise: (id, exercise) => {
      return saveExercise(id, exercise);
    },

    saveAnswer: (id, answer, dateCompleted, skipAnalytics) => {
      return saveAnswer(id, answer, dateCompleted, skipAnalytics);
    },

    changeExerciseEvent: (id, eventId) => {
      return changeExerciseEvent(id, eventId);
    },

    duplicateEvent: (eventId) => {
      return duplicateEvent(eventId);
    },

    duplicateExercise: (exerciseId) => {
      return duplicateExercise(exerciseId);
    },

    deleteExercise: (exerciseId) => {
      return deleteExercise(exerciseId);
    },

    deleteEvent: (eventId) => {
      return deleteEvent(eventId);
    },

    deleteCompletedAnswers: (exerciseId, answerIds) => {
      return deleteCompletedAnswers(exerciseId, answerIds);
    },

    deleteClass: (classId) => {
      return deleteClass(classId);
    },

    deleteVetVisit: (vetVisitId) => {
      return deleteVetVisit(vetVisitId);
    },

    setAttendance: (isAttending, eventId) => {
      return setAttendance(isAttending, eventId);
    },

    saveClass: (id, trainingClass, prevDate) => {
      return saveClass(id, trainingClass, prevDate);
    },

    saveVetVisit: (id, vetVisit, prevDate) => {
      return saveVetVisit(id, vetVisit, prevDate);
    },

    setTrainerComments: (answerId, comments) => {
      return setTrainerComments(answerId, comments);
    },


    // -------------------------------
    // deployment functions
    // -------------------------------

    saveDeployment: (id, deployment, previousDate, skipAnalytics) => {
      return saveDeployment(id, deployment, previousDate, skipAnalytics);
    },

    deleteDeployment: (deploymentId) => {
      return deleteDeployment(deploymentId);
    },


    // -------------------------------
    // tracking functions
    // -------------------------------

    createDeploymentTrack: (model) => {
      return createDeploymentTrack(model);
    },

    createLeadTrack: (model) => {
      return createLeadTrack(model);
    },

    createFollowTrack: (model) => {
      return createFollowTrack(model);
    }

  });
}